/**
 * RPC-style teleop command bus. Turns high-level command calls into DataChannel wire
 * messages. One class composing small single-concern pieces: `Dispatcher` (seq +
 * envelope + send), `MotionStream` (continuous drive streaming) and `CooldownGuard`
 * (edge-triggered debounce, one instance for posture, one for cargo). It knows nothing
 * about UI/DOM/refs — a `send(text)` transport is injected by the host (the session's
 * sendOperatorCommand), and the current operator speed caps (owned by teleop-state) are
 * injected as a `getSpeedLimits()` function, consumed by `MotionStream` for its own
 * norm→SI scaling.
 *
 * Trusts its inputs — no defensive re-validation. `posture`'s `PostureKey` is a TS
 * literal union; `cargo`/`camera`'s box id and mount are already normalized where the
 * robot-config JSON enters the system (teleop-state's normalizeCargoBoxesPayload /
 * normalizeCamerasPayload). If a future caller bypasses both (e.g. via `as`), a bad
 * value flows straight to the wire instead of being rejected — accepted trade-off,
 * since no such caller exists today.
 */

import { CARGO, POSTURE } from "./config";
import { CooldownGuard } from "./cooldown";
import { Dispatcher } from "./dispatcher";
import { MotionStream } from "./motion-stream";
import { v4 } from "uuid";

import type {
  CargoAction,
  MotionProfile,
  PostureKey,
  SpeedAxis,
  VelocityDemand,
} from "../../types/commands";

export interface CreateTeleopCommandsOptions {
  /** Transport that delivers the JSON wire message; returns true when sent over an open channel. */
  send: (text: string) => boolean;
  /**
   * Current operator speed caps in SI, read on every `setVelocity()` tick — owned by
   * teleop-state (`robotState.speedLimits.getUserSpeedLimits`), injected here so this
   * module never imports teleop-state. Defaults to no scaling (cap 1 on every axis) when
   * omitted, e.g. in tests that don't care about limits.
   */
  getSpeedLimits?: () => Record<SpeedAxis, number>;
  /**
   * Robot motion kinematics, read on every `setVelocity()` tick — owned by teleop-state
   * (`robotState.speedLimits.getMotionProfile`), injected for the same reason as
   * `getSpeedLimits`. Defaults to "holonomic" (today's vx/vy/vyaw wire shape) when
   * omitted.
   */
  getMotionProfile?: () => MotionProfile;
}

/**
 * A single robot-config-advertised command entry, as executed by `executeCommand` —
 * deliberately just the fields this module needs (not the richer
 * `RobotCommandEntry`/`CommandCatalog` shape teleop-state's command-catalog.ts
 * produces), so this module keeps zero dependency on teleop-state (see
 * module-boundaries.test.js). A `RobotCommandEntry` satisfies this shape structurally
 * — the UI layer passes catalog entries straight through with no adapter needed.
 */
export interface AdvertisedCommand {
  type: string;
  mode: "stream" | "edge";
  params?: Record<string, unknown>;
}

export interface TeleopCommands {
  /**
   * Continuous drive update. Sends `motion.velocity` while the stick demand is above epsilon,
   * and emits a single `motion.stop` on the active → idle transition.
   * @returns true when a message was delivered over an open channel
   */
  setVelocity(norm: VelocityDemand): boolean;
  /** Hard stop (estop, unmount). Idempotent — safe to call at any time. */
  stop(): boolean;
  /** Edge-triggered posture command. Interrupts active motion first, enforces cooldown. */
  posture(key: PostureKey): boolean;
  /** Edge-triggered cargo open/close. Enforces a per-box cooldown shared by both actions. */
  cargo(boxId: string, action: CargoAction): boolean;
  /**
   * Execute a validated edge entry straight out of a robot-config command catalog
   * (see teleop-state's command-catalog.ts) — the dynamic counterpart of `posture`/
   * `cargo` for command types that aren't fixed literals (e.g. a robot-specific
   * `posture.sit`). Shares the same cooldown guards as `posture`/`cargo`, so mixing
   * the fixed and dynamic APIs on the same robot still debounces correctly.
   */
  executeCommand(command: AdvertisedCommand): boolean;
  /** Camera enable/disable control. */
  camera(mount: string, enabled: boolean): boolean;
}

export class TeleopCommandBus implements TeleopCommands {
  private dispatcher: Dispatcher;
  private motion: MotionStream;
  private postureGuard = new CooldownGuard(POSTURE.cooldownMs);
  private cargoGuard = new CooldownGuard(CARGO.cooldownMs);

  constructor(
    send: (text: string) => boolean,
    getSpeedLimits: () => Record<SpeedAxis, number>,
    getMotionProfile: () => MotionProfile
  ) {
    this.dispatcher = new Dispatcher(send);
    this.motion = new MotionStream(getSpeedLimits, this.dispatcher, getMotionProfile);
  }

  public setVelocity(norm: VelocityDemand): boolean {
    return this.motion.setVelocity(norm);
  }

  public stop(): boolean {
    return this.motion.stop();
  }

  public posture(key: PostureKey): boolean {
    if (!this.postureGuard.ready()) return false;
    if (this.motion.isStreaming()) this.motion.stop();
    const ok = this.dispatcher.dispatchSeq({
      event: "send-posture",
      payload: { message_id: v4(), type: POSTURE.types[key] },
    });
    if (ok) this.postureGuard.mark();
    return ok;
  }

  public cargo(boxId: string, action: CargoAction): boolean {
    if (!this.cargoGuard.ready(boxId)) return false;
    const ok = this.dispatcher.dispatchSeq({
      event: "send-cargo",
      payload: { message_id: v4(), type: `cargo.${action}`, box_id: boxId },
    });
    if (ok) this.cargoGuard.mark(boxId);
    return ok;
  }

  public executeCommand(command: AdvertisedCommand): boolean {
    if (!command || command.mode !== "edge") return false;
    if (command.type === "motion.stop") return this.stop();
    if (command.type.startsWith("posture.")) {
      // Shares `postureGuard` with `posture()` — one cooldown window regardless of
      // which API a caller reaches a posture command through.
      if (!this.postureGuard.ready()) return false;
      if (this.motion.isStreaming()) this.motion.stop();
      const ok = this.dispatcher.dispatchSeq({
        event: "send-posture",
        payload: { message_id: v4(), type: command.type },
      });
      if (ok) this.postureGuard.mark();
      return ok;
    }
    if (command.type === "cargo.open" || command.type === "cargo.close") {
      const boxId = command.params?.box_id;
      if (typeof boxId !== "string") return false;
      return this.cargo(boxId, command.type === "cargo.open" ? "open" : "close");
    }
    return false;
  }

  public camera(mount: string, enabled: boolean): boolean {
    return this.dispatcher.dispatch({ event: "camera-control", payload: { mount, enabled } });
  }
}

/**
 * Create a command bus bound to a DataChannel transport.
 */
export function createTeleopCommands({
  send,
  getSpeedLimits = () => ({ vx: 1, vy: 1, vyaw: 1, steer_angle: 1 }),
  getMotionProfile = () => "holonomic",
}: CreateTeleopCommandsOptions): TeleopCommands {
  return new TeleopCommandBus(send, getSpeedLimits, getMotionProfile);
}
